import React, { useContext, useEffect, useState } from 'react';
import loadable from '@loadable/component';
import CityPicker from './CityPicker';
import utils from '../utils';
import InputBox from '../InputBox';
import ModifySearchContext from '../ModifySearchContext';
import InfoStrip from '../../InfoStrip';
import {
  geolocationHandler,
  getLocationLocalStorage,
  setLocationLocalStorage,
} from '../../../utils/geoLocationUtils';
import { getMMT_HLD_OBJ } from '../../../utils/appUtils';
import {
  AUTO_DETECT,
  CITY_TYPE,
  CURR_LOCATION,
  FROM_MANUAL,
  FROM_SUGGEST,
  GEO_LOCATION_STORAGE,
} from '../constants';
import { getSearchData } from '../../../service/geoLocationService';
import { getUrlParamsMapV2 } from '../../../utils/browserUtils';

const LocationModals = loadable(
  () => /* webpackChunkName: 'location modal' */ import('./LocationModals')
);

const SelectCityWithLocation = ({
  label = 'FROM CITY',
  cities = [],
  name = '',
  next = false,
  openBox = null,
  searchButtonClicked = false,
  setsearchButtonClicked,
  landOnly = null,
  search,
  onNext,
  onFocus,
  onSelect = () => {},
  omnitureCapture,
  omnitureLoad,
  handleSearchClick,
  departureCity,
  setCityAction,
  setFromCityData,
  autoDetectEnabled = true,
  icon,
}) => {
  const [nearByCitiesList, setNearByCitiesList] = useState(null);
  const [modalMessage, setModalMessage] = useState('');
  const [defaultDepartureCityCoords, setDefaultDepartureCityCoords] = useState(null);
  const [searchType, setSearchType] = useState(null);
  const [autoPopup, setAutoPopup] = useState(false);
  const [showToastMessage, setShowTestMessage] = useState(false);
  const [currentCityLocation, setCurrentCityLocation] = useState('');
  const { setActiveControl } = useContext(ModifySearchContext);
  const preSelectedCityObj = getLocationLocalStorage(GEO_LOCATION_STORAGE) || {};
  let timeoutId = null;
  const urlParams = getUrlParamsMapV2();

  useEffect(() => {
    const getCoord = geolocationHandler(() => {});
    if (defaultDepartureCityCoords === null && autoDetectEnabled) {
      getCoord.getCurrentPosition((position, error) => {
        if (position) {
          setDefaultDepartureCityCoords(position.coords);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (openBox !== null) {
      setActiveControl(openBox);
      setsearchButtonClicked(false);
    }
  }, [openBox, searchButtonClicked]);

  useEffect(() => {
    const goeLocationPokus = urlParams?.glp
      ? urlParams.glp === 'true'
      : getMMT_HLD_OBJ().showGeoLocation;
    if (
      search?.default?.fromCityCode &&
      defaultDepartureCityCoords &&
      autoDetectEnabled &&
      goeLocationPokus &&
      departureCity.toLowerCase() !==
        getLocationLocalStorage(GEO_LOCATION_STORAGE)?.city?.toLowerCase()
    ) {
      getCityNameFromGeo();
    }
  }, [defaultDepartureCityCoords]);

  const searchClickHandler = cityObj => {
    handleSearchClick(cityObj);
  };

  const showPermissionToastHandler = () => {
    setShowTestMessage(true);
  };

  const hidePermissionToastHandler = () => {
    setShowTestMessage(false);
    clearTimeout(timeoutId);
  };

  if (showToastMessage) {
    timeoutId = setTimeout(() => {
      setShowTestMessage(false);
    }, 5000);
  }

  const getCityNameFromGeo = async () => {
    const searchedDataResponse = await getSearchData(
      defaultDepartureCityCoords,
      search?.default?.fromCityCode
    );
    const { data } = searchedDataResponse;
    const { success, error, searchDepatureCityList } = data;
    if (success) {
      const { city: cityObj, message = null, nearByCities = null } = searchDepatureCityList?.[0];
      let city = null;
      if (cityObj) {
        city = { airportCode: null, ...cityObj };
      }
      localStorage.setItem(GEO_LOCATION_STORAGE, JSON.stringify(city));
      if (getLocationLocalStorage(GEO_LOCATION_STORAGE) === null) {
        setModalMessage(message);
        setCurrentCityLocation(city);
        setNearByCitiesList(nearByCities);
        setAutoPopup(true);
      }
    }
  };

  const closeModalHandler = modalData => {
    const currentLocation = JSON.parse(localStorage.getItem(GEO_LOCATION_STORAGE));
    const eVar49Value = `${currentLocation?.cityName} | ${searchType} | ${
      modalData?.cancel ? modalData?.cancel?.cityName : modalData?.cityName
    } `;
    const prop54Value = `Popup_geo | ${modalData.messageTemplate} | ${modalData.ctaLeft} | ${
      modalData?.cancel?.airportCode ? CITY_TYPE.AIRPORT : CITY_TYPE.NON_AIRPORT
    }`;
    omnitureCapture('location_modal', {
      prop54: prop54Value,
      eVar49: eVar49Value,
    });
    setDefaultDepartureCityCoords('');
    setModalMessage('');
    if (modalData?.cancel?.airportCode === null && modalData?.reload) {
      setLocationLocalStorage(
        GEO_LOCATION_STORAGE,
        JSON.stringify({
          cityName: modalData?.cancel?.cityName,
          locusId: modalData?.cancel?.locusId,
          from: GEO_LOCATION_STORAGE,
          airportCode: modalData?.cancel?.airportCode,
        })
      );
      searchClickHandler({
        cityName: modalData?.cancel?.cityName,
        locusId: modalData?.cancel?.locusId,
        from: autoPopup ? AUTO_DETECT : CURR_LOCATION,
        airportCode: modalData?.cancel?.airportCode,
      });
    } else {
      setLocationLocalStorage(
        GEO_LOCATION_STORAGE,
        JSON.stringify({
          cityName: search?.default?.fromCity,
          locusId: search?.default?.fromCityCode,
          from: 'cancelGeolocation',
          airportCode: modalData?.airportCode,
        })
      );
    }
    setAutoPopup(false);
  };

  const changeBtnHandler = modalData => {
    const currentLocation = JSON.parse(localStorage.getItem(GEO_LOCATION_STORAGE));
    const eVar49Value = `${currentLocation?.cityName} | ${searchType} | ${modalData?.update?.cityName}`;
    const prop54Value = `Popup_geo | ${modalData.messageTemplate} | ${modalData.ctaRight} | ${
      modalData?.update?.airportCode ? CITY_TYPE.AIRPORT : CITY_TYPE.NON_AIRPORT
    }`;
    omnitureCapture('location_modal', {
      prop54: prop54Value,
      eVar49: eVar49Value,
    });
    if (modalData?.reload) {
      setLocationLocalStorage(
        GEO_LOCATION_STORAGE,
        JSON.stringify({
          cityName: modalData?.update?.cityName,
          locusId: modalData?.update?.locusId,
          from: GEO_LOCATION_STORAGE,
          airportCode: modalData?.airportCode,
        })
      );
      searchClickHandler({
        cityName: modalData?.update?.cityName,
        locusId: modalData?.update?.locusId,
        from: autoPopup ? AUTO_DETECT : CURR_LOCATION,
        airportCode: modalData?.update?.airportCode,
      });
    } else {
      setLocationLocalStorage(GEO_LOCATION_STORAGE, JSON.stringify({ data: 'no selection' }));
      setModalMessage('');
    }
    setAutoPopup(false);
  };

  const handleChange = (city = {}, fieldName, saveToLocalStorage = false) => {
    let eVar49Value = '';
    if (fieldName === 'fromCity') {
      const searchedType = city?.from
        ? city.from === GEO_LOCATION_STORAGE || city?.from === ''
          ? FROM_SUGGEST
          : city.from
        : undefined;
      if (searchedType === FROM_MANUAL) {
        const currentLocation = JSON.parse(localStorage.getItem(GEO_LOCATION_STORAGE));
        eVar49Value = `${currentLocation?.cityName} | ${searchedType} | ${city.name}`;
      } else {
        setSearchType(searchedType);
      }
      omnitureCapture('edit_fromCity', {
        eVar49: eVar49Value,
        prop1: `${city?.name} | ${city?.data === null ? 'Non Airport' : 'Airport'}`,
      });
    } else {
      omnitureCapture('edit_dest');
    }
    if (city.from === GEO_LOCATION_STORAGE) {
      setDefaultDepartureCityCoords('');
    } else {
      if (document.getElementsByClassName('nonAirportInfo')?.[0]) {
        document.getElementsByClassName('nonAirportInfo')[0].classList.add('hide');
      }
      setCurrentCityLocation('');
      setCityAction(city, fieldName);
    }
    if (city.from && saveToLocalStorage) {
      setLocationLocalStorage(
        GEO_LOCATION_STORAGE,
        JSON.stringify({
          cityName: city?.name,
          locusId: city?.locusId,
          from: city.from,
          airportCode: city?.data,
        })
      );
      setFromCityData &&
        setFromCityData({
          cityName: city?.name,
          from: FROM_MANUAL,
          airportCode: city?.data,
        });
    }
  };

  const handleCityChange = (city, saveToLocalStorage) => {
    onSelect(city);
    if (next && onNext) {
      onNext();
    }
    setActiveControl(next || null);
    handleChange(city, name, saveToLocalStorage);
  };

  const value =
    defaultDepartureCityCoords && currentCityLocation
      ? currentCityLocation
      : { cityName: departureCity };
  return (
    <>
      <InputBox
        name={name}
        value={utils.getDisplayValue('citypicker', value?.cityName)}
        label={label}
        onFocus={onFocus}
        disableInput={landOnly}
        tooltipText={`This package is starting from ${utils.getDisplayValue(
          'citypicker',
          value?.cityName
        )}.<br />Starting city of this package cannot be changed.`}
        icon={icon}
      >
        <CityPicker
          value={value?.cityName}
          cityObj={value}
          handleChange={handleCityChange}
          cities={cities}
          showPermissionToastHandler={showPermissionToastHandler}
          setModalMessage={setModalMessage}
          setCurrentCityLocation={setCurrentCityLocation}
          setNearByCitiesList={setNearByCitiesList}
          modalMessage={modalMessage}
          omnitureCapture={omnitureCapture}
        />
      </InputBox>
      {showToastMessage && (
        <InfoStrip
          message="Please enable location permission from settings and try again!"
          bgColorClass="errorStrip"
          classes="locationError"
          closeBtnHandler={hidePermissionToastHandler}
        />
      )}
      {modalMessage && (
        <LocationModals
          message={modalMessage}
          onCloseHandler={closeModalHandler}
          onChangeHandler={changeBtnHandler}
          citiesList={currentCityLocation?.airportCode === null ? nearByCitiesList : []}
          currentCityObj={currentCityLocation}
          preselectedCityObj={JSON.parse(JSON.stringify(preSelectedCityObj))}
          nearByCityObj={nearByCitiesList}
          omnitureLoad={omnitureLoad}
        />
      )}
    </>
  );
};

export default SelectCityWithLocation;
